import { apiUrl } from "../api/endpoints";
import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import Spinner from "../Components/Spinner";
import axios from "axios";
import { useAuth } from "../hooks/AuthProvider";
import { STATIC_ICONS } from "../Constants";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddTemplate } from "./AddTemplate";
import { useNavigate } from "react-router-dom";
import "../Components/modal.css";
import "./edit.css";
import { deleteWithIcon, deleteMultiWithIcon } from "../lib/actions";
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { TemplateIcon } from "../Icons";

import { LanguageContext } from "../context/LanguageContext";
import templateTranslations from "../languages/template.json";
import { getErrorMessage } from "../utils/errorUtils";
import AppDialog from "../Components/AppDialog";
import { normalizeTemplateListRow } from "../utils/listRowIds";

export const Templates = () => {
  const { permissions } = useAuth();
  const canView = permissions["view_templates"];
  const canAdd = permissions["add_new_templates"];
  const canDelete = permissions["delete_templates"];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const navigate = useNavigate();
  const pagingoptions = [10, 20, 50, 100];

  const { lang } = useContext(LanguageContext);
  const t = templateTranslations[lang];
  const isRtl = ["ar", "ur"].includes(lang);

  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "alert", // "alert" | "confirm"
    variant: "info", // "info" | "danger" | "error"
    icon: "alert", // "alert" | "delete"
    title: "",
    message: "",
    confirmText: t.buttonOk || "OK",
    cancelText: t.buttonCancel || "Cancel",
    showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: opts?.type || "alert",
          variant: opts?.variant || "info",
          icon: opts?.icon || "alert",
          title: opts?.title || "",
          message: opts?.message || "",
          confirmText: opts?.confirmText || t.buttonOk || "OK",
          cancelText: opts?.cancelText || t.buttonCancel || "Cancel",
          showCancel: Boolean(opts?.showCancel),
        });
      }),
    [t.buttonCancel, t.buttonOk]
  );

  const showAlert = useCallback(
    async (type, message) => {
      const variant =
        type === "error" ? "error" : type === "warning" ? "info" : "info";

      await alert({
        type: "alert",
        variant,
        icon: "alert",
        title:
          type === "error"
            ? t?.alert?.errorTitle || "Error"
            : type === "warning"
            ? t?.alert?.warningTitle || "Warning"
            : t?.alert?.infoTitle || "Info",
        message: message || "",
        confirmText: t.buttonOk || "OK",
      });
    },
    [alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.infoTitle, t.buttonOk]
  );

  const colors = {
    red: "rgb(236, 69, 78)",
  };

  const handleRowClick = (params) => {
    navigate("/configuration/templates/details/" + params.row.id);
  };

  const baseColumns = [
    {
      field: "icon",
      headerName: "",
      width: 60,
      align: "center",
      renderCell: (params) =>
        params.row.icon ? (
          <img
            src={STATIC_ICONS + params.row.icon}
            alt=""
            style={{
              width: "calc(32px * var(--font-size-multiplier))",
              height: "calc(32px * var(--font-size-multiplier))",
              marginTop: "8px",
              alignItems: "center",
            }}
          />
        ) : (
          <TemplateIcon
            color={"var(--accent)"}
            style={{
              fontSize: "calc(var(--icon-size) * var(--font-size-multiplier))",
            }}
          />
        ),
    },
    {
      field: "name",
      headerName: t.column.name,
      width: 400,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <span
          style={{
            fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
          }}
        >
          {params.row.name}
        </span>
      ),
    },
    {
      field: "type",
      headerName: t.column.type || "Type",
      width: 190,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "device_count",
      headerName: t.column.deviceCount,
      width: 200,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <span
          style={{
            fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
          }}
        >
          {params.row.device_count}
        </span>
      ),
    },
    {
      field: "tid",
      headerName: t.column.tid,
      width: 200,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <span
          style={{
            fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
          }}
        >
          {params.row.tid}
        </span>
      ),
    },
  ];

  const columns = canDelete
    ? [
        {
          field: "actions",
          headerName: t.column.actions,
          type: "actions",
          width: 100,
          getActions: (params) => [
            <GridActionsCellItem
              title={t.delete}
              icon={
                <DeleteIcon
                  style={{
                    color: "red",
                    fontSize: "calc(var(--icon-size) * var(--font-size-multiplier))",
                  }}
                />
              }
              showInMenu
              label={t.delete}
              onClick={() =>
                deleteWithIcon(params.row.id, params.row.icon, "template", fetchTemplates, alert)
              }
            />,
          ],
        },
        ...baseColumns,
      ]
    : baseColumns;

  const fetchTemplates = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      setRowCount(0);
      return;
    }

    setLoading(true);
    try {
      const activeSort = sortModel && sortModel.length > 0 ? sortModel[0] : { field: "name", sort: "asc" };
      const result = await axios.get(apiUrl(`template/list`), {
        params: {
          page,
          pageSize,
          sortField: activeSort.field || "name",
          sortOrder: activeSort.sort || "asc",
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });
      setRows(
        Array.isArray(result.data?.data)
          ? result.data.data.map(normalizeTemplateListRow)
          : []
      );
      setRowCount(result.data?.pagination?.total ?? result.data?.data?.length ?? 0);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      await showAlert("error", `${t.failedToLoadTemplates || "Failed to load templates."} ${errorMessage}`);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, filterModel, page, pageSize, showAlert, sortModel, t.failedToLoadTemplates]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const [selectedRows, setSelectedRows] = React.useState([]);

  const handleMultiDelete = async (ids) => {
    if (!canDelete) {
      await showAlert("error", t.deleteTemplatesPermissionError || "You do not have permission to delete templates.");
      return;
    }

    const icons = ids.map((id) => rows.find((row) => row.id === id)?.icon);
    await deleteMultiWithIcon(ids, icons, "template", fetchTemplates, alert);
    setSelectedRows([]);
  };

  const selectActions = canDelete
    ? [
        {
          title: t.delete,
          icon: (
            <DeleteIcon
              style={{
                color: colors.red,
                marginInlineEnd: "20px",
                fontSize: "calc(var(--icon-size) * var(--font-size-multiplier))",
              }}
            />
          ),
          action: (ids) => handleMultiDelete(ids),
        },
      ]
    : [];

  if (!canView) return <div>{t.noPermission}</div>;

  return (
    <div
      className="configuration devices-page"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      {openAddModal && (
        <AddTemplate
          bg={"var(--cell-background)"}
          fg={"var(--cell-color)"}
          ft={"var(--header-background)"}
          btnPrimary={"rgb(219, 245, 236)"}
          btnSecondary={"var(--accent)"}
          textColor={"var(--header-color)"}
          open={openAddModal}
          setOpen={setOpenAddModal}
          refresh={fetchTemplates}
        />
      )}

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title">{t.templatesPageTitle}</h2>
          </div>

          {canAdd ? (
            <button className="devices-primary-btn" onClick={() => setOpenAddModal(true)}>
              <span>{t.newTemplate}</span>
            </button>
          ) : null}
        </div>

        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div
            className="table-wrapper"
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <DataGridTable
              columns={columns}
              rows={rows}
              pagingoptions={pagingoptions}
              idcolumn={"id"}
              handleRowClick={handleRowClick}
              checkboxSelection={true}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey="templates"
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
              sx={{
                fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            {t.noData}
          </div>
        )}
      </div>
    </div>
  );
};
